import { defineStore } from 'pinia'
import { computed, inject, reactive, ref, toValue } from 'vue'
import { router, usePage } from '@inertiajs/vue3'
import axios from 'axios'
import { echo } from '@/echo'
import { Error, makeRuleset, parseAxiosError, Round, Ruleset, Session, useSessionData } from '@/objects'
import { AxiosError } from 'axios'
import { handleError } from './AppStore'
import { roundsCount } from '@/utils'

export type PartData = {
    session: Session,
    id: number,
    // Id of the participant's latest message still awaiting a reply at page load, or -1.
    // Needed because a message that arrived before the consultant subscribed comes in via
    // the initial data (not the MessageSent event), so its id would otherwise be unknown.
    pendingMsgId: number,
    // A message that arrived while our reply to the previous one was still in flight (the
    // reply is broadcast to the participant before our POST resolves, so a quick participant
    // can send the next one first). Applied as the new pending message once the POST is done,
    // so it lands after the reply in the transcript instead of being dropped.
    queuedMsg?: { id: number, body: string } | null,
    disconnected?: boolean
}

//TODO: Add pendingMessage property to Session class and have it compute this
// (not vital I'm just pignolo af - Terenz)

// Scan the raw session payload for the last message with no reply. A participant can only
// send again after each reply, so at most one such message exists at a time.
function pendingMessageId(chatSession: any): number {
    const rounds = [
        ...(chatSession?.["phase1Rounds"] ?? []),
        ...(chatSession?.["phase2Rounds"] ?? []),
    ]

    let pending = -1
    for (const round of rounds) {
        for (const msg of (round?.["messages"] ?? [])) {
            if (msg?.["reply"] == null) {
                pending = msg?.["id"] ?? -1
            }
        }
    }
    return pending
}

type ConsultantData = {
    id: number,
    email: string,
    participants: {[id: number]: PartData},
    selectedPartId: number
}

export const useConsultantStore = defineStore('consultant', () => {
    //state
    const state = reactive<ConsultantData>({
        id: -1,
        email: '',
        participants: {},
        selectedPartId: -1
    })

    /**
     * Callback to run when a participant's message is received
     * Runs after checking if it's a valid signal and after updating the state
     */
    const onPartMessage = ref<(partId: number, msgText: string, msgId: number) => void>()

    /**
     * Callback to run when a participant's action is received
     * Runs after checking if it's a valid signal and after updating the state
     */
    const onPartAction = ref<(partId: number) => void>()

    /**
     * Callback to run when a participant's action is received
     * Runs after checking if it's a valid signal and after updating the state,
     * and runs for each participant involved in the vote
     */
    const onPartVoted = ref<(partId: number, rs: Ruleset) => void>()

    const onDisconnected = ref<(partId: number) => void>()

    const onError = ref<(err: Error) => void>()

    //getters
    const currentParticipant = computed<PartData>(() => {
        if (state.selectedPartId <= 0) {
            return null
        }
        
        return state.participants[state.selectedPartId]
    })

    const currentSession = computed<Session>(() => currentParticipant.value?.session ?? null)

    const currentRound = computed<Round>(() => {
        const session = currentSession.value;
        if (!session)
            return null;
        
        return session.currentRound
    })
    
    //actions
    function initialize() {
        const data = usePage().props.consultantData;
        console.log("Session data:")
        console.log(data)

        if (!data) return;

        state.id = data["id"]
        state.email = data["email"] ?? ''

        loadParticipants(data["participants"])

        // The page payload is rendered before the websocket subscription is up, so any event
        // broadcast in between (a participant messaging right as we load, or while the
        // connection was down) is never delivered. Refetch the payload once the channel is
        // confirmed and reconcile: subscribed() fires again after every reconnection.
        echo.private(`consultant.${state.id}`).subscribed(() => {
            router.reload({
                only: ['consultantData'],
                onSuccess: (page) => {
                    const fresh = page.props.consultantData
                    if (fresh) loadParticipants(fresh["participants"], true)
                },
            })
        })

        listenTo("MessageSent", (e) => {
            const partId = e["message"].participant_id
            const part = state.participants[partId]

            if (!part) {
                console.warn(`Message from unknown participant ${partId}!`)
                return
            }

            const msgTxt = e["message"].body
            const msgId = parseInt(e["message"].id)

            if (part.pendingMsgId >= 0) {
                if (msgId === part.pendingMsgId) {
                    console.warn(`Participant ${partId} was awaiting a reply!`)
                    return
                }
                console.log(`Participant ${partId} sent '${msgTxt}' while our reply is in flight; queued`)
                part.queuedMsg = { id: msgId, body: msgTxt }
                return
            }

            part.pendingMsgId = msgId
            part.session.currentRound.addMessage(msgTxt)

            console.log(`Participant ${partId} says: '${msgTxt}'`)

            if (onPartMessage.value) {
                onPartMessage.value(partId, msgTxt, msgId)
            }
        })

        listenTo("ActionSent", (e) => {
            const partId = e["action"].participant_id
            const part = state.participants[partId]

            if (!part) {
                console.warn(`Action from unknown participant ${partId}!`)
                return
            }

            if (!part.session.currentRound) {
                console.warn(`Action for participant ${partId} with no active round!`)
                return
            }

            // We just need to store that the participant has chosen, not the actual choice
            // We also don't need to know what their opponent is doing, the participant UI
            // will make sure they don't text their consultant until the next round
            part.session.currentRound.choices = ["A", "A"]

            // Mirror the participant's Play.vue advance(): the phase holds only the rounds
            // played so far plus the open one, so open the next round while rounds remain
            // and mark the phase complete only once ROUNDS_PER_PHASE have been played.
            const phase = part.session.currentPhase
            if (phase.rounds.length < roundsCount()) {
                phase.addRound(new Round(phase.rounds.length, [], part.session.hasChat))
            } else {
                phase.completed = true
            }

            console.log(`Participant ${partId} has chosen`)

            if (onPartAction.value) {
                onPartAction.value(partId)
            }
        })

        listenTo("VoteComplete", (e) => {
            const allVoters = (e["voters"] as Array<any>)?.map((v) => Number(v)) ?? null
            if (!allVoters) {
                console.warn(`Invalid or empty list of voters!`, e["voters"])
                return
            }

            const parts = allVoters
                .filter((v) => v in state.participants)
                .map((v) => state.participants[v])

            if (!parts) {
                console.warn(`Received voteComplete for no known participant!`)
                return
            }

            const ruleset = makeRuleset(e["ruleset"])

            parts.forEach((part) => {
                part.session.voteDone = true
                part.session.phase1.evaluated = true
                part.session.votedRuleset = ruleset
                part.session.phase2.ruleset = ruleset

                if (onPartVoted.value) {
                    onPartVoted.value(part.id, ruleset)
                }
            })
        })

        listenTo("Disconnected", (e) => {
            const partId = e["part_id"]
            const part = state.participants[partId]

            if (!part) {
                console.warn(`Disconnection from unknown participant ${partId}!`)
                return
            }

            if (part.disconnected) {
                console.warn(`Participant ${partId} had already disconnected!`)
                return
            }

            part.disconnected = true

            if (onDisconnected.value) {
                onDisconnected.value(part.id)
            }
        })
    }

    // Build (or rebuild) the per-participant state from the page payload. When reconciling,
    // a participant whose reply is pending or in flight is left alone: the local state is
    // at least as fresh as the payload, and swapping it could double-answer the message.
    function loadParticipants(participants: Object[], reconcile = false) {
        participants.forEach((part: Object) => {
            const existing = state.participants[part["id"]]
            if (reconcile && existing && existing.pendingMsgId >= 0) {
                return
            }

            const tmp = useSessionData()

            tmp.stateInit(part["chatSession"])

            if (!tmp.state.phase1.currentRound) {
                tmp.state.phase1.completed = true
            }

            const pendingMsgId = pendingMessageId(part["chatSession"])
            if (reconcile && pendingMsgId >= 0) {
                console.log(`Participant ${part["id"]} had a pending message we never received; recovered`)
            }

            state.participants[part["id"]] = {
                session: tmp.state,
                id: part["id"],
                pendingMsgId,
                disconnected: existing?.disconnected,
            }
        });
    }

    function selectParticipant(id: number) {
        state.selectedPartId = id;
    }

    function handleErr(caller: string, err: AxiosError) {
        handleError(err, toValue(onError), caller)
    }

    async function sendReply(messageBody: string, partId: number) {
        const part = state.participants[partId]

        if (!part) {
            console.warn(`Replying to unknown participant ${partId}!`)
            return
        }

        if (part.pendingMsgId < 0) {
            console.warn(`Participant ${partId} has no pending messages!`)
            return
        }

        try {
            const { data } = await axios.post('/replies', { 
                message_id: part.pendingMsgId,
                body: messageBody 
            })

            part.pendingMsgId = -1
            part.session.currentRound.addMessage(messageBody)

            if (part.queuedMsg) {
                const queued = part.queuedMsg
                part.queuedMsg = null
                part.pendingMsgId = queued.id
                part.session.currentRound.addMessage(queued.body)

                if (onPartMessage.value) {
                    onPartMessage.value(partId, queued.body, queued.id)
                }
            }

            return { ok: true, message: data }

        } catch (err) {
            handleErr('sendReply', err)
            
            return {
                ok: false,
                message: 'Ops! Qualcosa è andato storto. Riprova più tardi.',
            }
        }
    }

    function listenTo(event: string, callback: (e: Object) => void) {
        return echo.private(`consultant.${state.id}`).listen(event, callback)
    }

    return {
        currentParticipant,
        currentSession,
        currentRound,
        state,
        onPartMessage,
        onPartAction,
        onError,
        onDisconnected,
        initialize,
        selectParticipant,
        sendReply,
        listenTo
    }
})
